import { useEffect, useState } from 'react';
import {
  KIMBARK_ROOMS,
  FLOOR_SVG,
  FLOOR_LABEL,
  type KimbarkFloor,
  type KimbarkRoom,
} from '../data/kimbarkRooms';

const IW = 1800;
const IH = 1350;

// Tight viewBox per floor — zooms into the actual room content with a small margin.
// Values in 1800×1350 coordinate space, derived from the bounding box of all room polygons.
const FLOOR_VIEWBOX: Record<KimbarkFloor, [number, number, number, number]> = {
  //              x_start  y_start  width   height
  basement: [290,    5,   1040,   670],
  ground:   [215,    8,   1310,  1095],
  upper:    [300,    5,   1205,  1130],
};

function polyPoints(poly: [number, number][]): string {
  return poly.map(([x, y]) => `${x * IW},${y * IH}`).join(' ');
}

function roomLabel(room: KimbarkRoom): string {
  if (room.dims) return `${room.name}\n${room.dims[0]}×${room.dims[1]} m`;
  return room.name;
}

function centroid(poly: [number, number][]): [number, number] {
  const sx = poly.reduce((a, [x]) => a + x, 0) / poly.length;
  const sy = poly.reduce((a, [, y]) => a + y, 0) / poly.length;
  return [sx * IW, sy * IH];
}

const FLOOR_COLORS: Record<KimbarkFloor, { fill: string; stroke: string; text: string; hover: string; inactive: string }> = {
  basement: { fill: '#1e3a5f', stroke: '#3b82f6', text: '#93c5fd', hover: '#60a5fa', inactive: '#334155' },
  ground:   { fill: '#1a3a2a', stroke: '#22c55e', text: '#86efac', hover: '#4ade80', inactive: '#1e3a2a' },
  upper:    { fill: '#3b1f5e', stroke: '#a855f7', text: '#d8b4fe', hover: '#c084fc', inactive: '#2d1b4e' },
};

interface FloorMapPanelProps {
  onTeleport: (pos: [number, number, number], lookAt: [number, number, number], floor: KimbarkFloor) => void;
  onClose: () => void;
  /** When set by the agent, this room name is highlighted with a pulsing fill. */
  highlightedRoomName?: string;
  /** When set, the panel auto-switches to this floor tab. */
  initialFloor?: KimbarkFloor;
}

export default function FloorMapPanel({ onTeleport, onClose, highlightedRoomName, initialFloor }: FloorMapPanelProps) {
  const [activeFloor, setActiveFloor] = useState<KimbarkFloor>(initialFloor ?? 'ground');
  const [hovered, setHovered] = useState<number | null>(null);

  useEffect(() => {
    if (initialFloor) setActiveFloor(initialFloor);
  }, [initialFloor]);

  const floors: KimbarkFloor[] = ['basement', 'ground', 'upper'];
  const visibleRooms = KIMBARK_ROOMS.filter(r => r.floor === activeFloor);
  const colors = FLOOR_COLORS[activeFloor];
  const [vx, vy, vw, vh] = FLOOR_VIEWBOX[activeFloor];

  const handleClick = (room: KimbarkRoom) => {
    if (room.viewpointWorld && room.objectWorld) {
      onTeleport(room.viewpointWorld, room.objectWorld, activeFloor);
    }
  };

  return (
    <div className="w-full rounded-xl bg-zinc-900/95 backdrop-blur-sm border border-white/10 shadow-2xl flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <span className="text-sm font-semibold text-white">Floor Map</span>
        <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Floor tabs */}
      <div className="flex border-b border-white/10">
        {floors.map(floor => (
          <button
            key={floor}
            onClick={() => { setActiveFloor(floor); setHovered(null); }}
            className={`flex-1 py-2 text-xs font-medium transition-colors ${
              activeFloor === floor
                ? `${floor === 'basement' ? 'text-blue-300 border-blue-400' : floor === 'ground' ? 'text-green-300 border-green-400' : 'text-purple-300 border-purple-400'} border-b-2`
                : 'text-white/40 hover:text-white/70'
            }`}
          >
            {FLOOR_LABEL[floor]}
          </button>
        ))}
      </div>

      {/* Single SVG — floor plan image + polygon overlay share the same zoomed viewBox */}
      <div className="bg-white/5">
        <svg
          viewBox={`${vx} ${vy} ${vw} ${vh}`}
          preserveAspectRatio="xMidYMid meet"
          className="block w-full h-auto"
        >
          <image
            href={FLOOR_SVG[activeFloor]}
            x={0}
            y={0}
            width={IW}
            height={IH}
            opacity={0.6}
            style={{ filter: 'invert(1) brightness(0.55)' }}
          />

          {visibleRooms.map((room, i) => {
            const isHovered = hovered === i;
            const isHighlighted = !!highlightedRoomName && room.name.toLowerCase() === highlightedRoomName.toLowerCase();
            const canTeleport = Boolean(room.viewpointWorld);
            const [cx, cy] = centroid(room.poly);

            const fill = isHighlighted
              ? colors.hover + '70'
              : isHovered && canTeleport ? colors.hover + '40'
              : canTeleport ? colors.fill + 'bb'
              : colors.inactive + '66';
            const stroke = isHighlighted ? colors.hover : isHovered && canTeleport ? colors.hover : canTeleport ? colors.stroke : '#475569';
            const strokeWidth = isHighlighted ? 3 : isHovered ? 2.5 : 1.5;

            return (
              <g
                key={i}
                style={{ cursor: canTeleport ? 'pointer' : 'default' }}
                onClick={() => handleClick(room)}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
              >
                <polygon
                  points={polyPoints(room.poly)}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth={strokeWidth}
                  vectorEffect="non-scaling-stroke"
                  style={{ transition: 'fill 120ms, stroke 120ms' }}
                />

                {roomLabel(room).split('\n').map((line, li) => (
                  <text
                    key={li}
                    x={cx}
                    y={cy + (li - (roomLabel(room).split('\n').length - 1) / 2) * 38}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={isHighlighted || isHovered ? 30 : 26}
                    fontWeight={isHighlighted || isHovered ? '600' : '400'}
                    fill={isHighlighted ? colors.hover : isHovered && canTeleport ? colors.hover : canTeleport ? colors.text : '#64748b'}
                    style={{ pointerEvents: 'none', userSelect: 'none' }}
                  >
                    {line}
                  </text>
                ))}

                {(isHovered && canTeleport) && (
                  <circle cx={cx} cy={cy - 55} r="10" fill={colors.hover} opacity="0.9" />
                )}
                {isHighlighted && !isHovered && (
                  <circle cx={cx} cy={cy - 55} r="8" fill={colors.hover} opacity="0.8" />
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Footer hint */}
      <div className="px-4 py-2 text-[10px] text-white/30 text-center border-t border-white/10">
        {hovered !== null && visibleRooms[hovered]
          ? visibleRooms[hovered].viewpointWorld
            ? `Teleport to ${visibleRooms[hovered].name}`
            : `${visibleRooms[hovered].name} — no teleport available`
          : 'Click a room to teleport there'}
      </div>
    </div>
  );
}
